import {
  decodeTorchOokSamples,
  probeTorchOokPreamble,
  TORCH_OOK_PROFILE,
} from '../protocol/torch-ook.js';

const ROI_RELOCK_FAILURE_CODES = Object.freeze(new Set([
  'LOW_CONTRAST',
  'NO_PREAMBLE',
  'NO_SYNC',
  'AUTO_TIMING_NOT_FOUND',
]));

/**
 * Timestamped flashlight receiver with a stable temporal ROI lock. The ROI
 * never follows the brightest current tile frame by frame. A bounded relock is
 * permitted only after repeated decoder evidence that no preamble is present.
 */
export class TorchReceiver {
  constructor(video, {
    chipMs,
    chipMsCandidates = null,
    chipMsMin = 60,
    chipMsMax = 260,
    chipMsStep = 20,
    autoProbePhaseSteps = 6,
    autoRefineSeeds = 3,
    autoDecodeCandidates = 5,
    autoBackoffBaseMs = 500,
    autoBackoffMaxMs = 4_000,
    analysisWidth = 160,
    gridColumns = 8,
    gridRows = 6,
    roi = null,
    roiLockFrames = 8,
    roiMinTemporalContrast = 0.08,
    roiHistoryFrames = 240,
    roiRelockFailures = 3,
    roiRelockCooldownMs = 5_000,
    roiRelockMinBufferMs = null,
    roiMaxRelocks = 3,
    maxBufferSamples = 20_000,
    maxBufferMs = 300_000,
    decodeIntervalMs = null,
    autoDecode = true,
    decoderOptions = {},
    frameReader = null,
    canvasFactory = defaultCanvasFactory,
    requestAnimationFrame = globalThis.requestAnimationFrame?.bind(globalThis),
    cancelAnimationFrame = globalThis.cancelAnimationFrame?.bind(globalThis),
  } = {}) {
    const fixedChipMs = Number.isFinite(chipMs) && chipMs > 0 ? Number(chipMs) : null;
    if (fixedChipMs === null && chipMs !== undefined && chipMs !== null && chipMs !== 'auto') {
      throw new RangeError('chipMs doit être > 0 ou "auto"');
    }
    assertIntegerAtLeast(analysisWidth, 16, 'analysisWidth');
    assertIntegerAtLeast(maxBufferSamples, 1, 'maxBufferSamples');
    assertPositive(maxBufferMs, 'maxBufferMs');
    assertIntegerBetween(roiRelockFailures, 1, 20, 'roiRelockFailures');
    assertPositive(roiRelockCooldownMs, 'roiRelockCooldownMs');
    if (roiRelockMinBufferMs !== null) assertPositive(roiRelockMinBufferMs, 'roiRelockMinBufferMs');
    assertIntegerBetween(roiMaxRelocks, 0, 20, 'roiMaxRelocks');
    this.video = video;
    this.timingMode = fixedChipMs === null ? 'auto' : 'fixed';
    this.chipMs = fixedChipMs;
    this.autoTimingPlan = fixedChipMs === null ? buildAutoTimingPlan({
      chipMsCandidates,
      chipMsMin,
      chipMsMax,
      chipMsStep,
    }) : null;
    assertIntegerBetween(autoProbePhaseSteps, 2, 24, 'autoProbePhaseSteps');
    assertIntegerBetween(autoRefineSeeds, 1, 6, 'autoRefineSeeds');
    assertIntegerBetween(autoDecodeCandidates, 1, 10, 'autoDecodeCandidates');
    assertPositive(autoBackoffBaseMs, 'autoBackoffBaseMs');
    assertPositive(autoBackoffMaxMs, 'autoBackoffMaxMs');
    if (autoBackoffMaxMs < autoBackoffBaseMs) throw new RangeError('autoBackoffMaxMs inférieur à autoBackoffBaseMs');
    this.autoProbePhaseSteps = autoProbePhaseSteps;
    this.autoRefineSeeds = autoRefineSeeds;
    this.autoDecodeCandidates = autoDecodeCandidates;
    this.autoBackoffBaseMs = autoBackoffBaseMs;
    this.autoBackoffMaxMs = autoBackoffMaxMs;
    this.estimatedChipMs = null;
    this.lastTimingSearch = null;
    this.autoSearchFailures = 0;
    this.nextAutoSearchAt = -Infinity;
    this.analysisWidth = analysisWidth;
    this.maxBufferSamples = maxBufferSamples;
    this.maxBufferMs = maxBufferMs;
    const cadenceChipMs = fixedChipMs || this.autoTimingPlan.minimumChipMs;
    this.decodeIntervalMs = decodeIntervalMs ?? Math.max(100, cadenceChipMs * 2);
    this.autoDecode = Boolean(autoDecode);
    this.decoderOptions = { ...decoderOptions };
    this.frameReader = frameReader;
    this.canvasFactory = canvasFactory;
    this.requestAnimationFrame = requestAnimationFrame;
    this.cancelAnimationFrame = cancelAnimationFrame;
    this.roiTracker = new StableTorchRoi({
      gridColumns,
      gridRows,
      explicitRoi: roi,
      lockFrames: roiLockFrames,
      minTemporalContrast: roiMinTemporalContrast,
      maxHistoryFrames: roiHistoryFrames,
    });
    this.roiRelockFailuresRequired = roiRelockFailures;
    this.roiRelockCooldownMs = roiRelockCooldownMs;
    this.roiRelockMinBufferOverrideMs = roiRelockMinBufferMs;
    this.roiMaxRelocks = roiMaxRelocks;
    this.roiRelockMinBufferMs = 0;
    this.roiRelockConsecutiveFailures = 0;
    this.roiRelockTriggeredCount = 0;
    this.lastRoiRelockAt = -Infinity;
    this.roiRelockDiscardedSamples = 0;
    this.lastRoiRelockDecision = null;
    this.#refreshRoiRelockGuard();
    this.samples = [];
    this.evictedSamples = 0;
    this.nonMonotonicFrames = 0;
    this.lastFrameTimestampMs = -Infinity;
    this.lastDecodeAt = -Infinity;
    this.lastDecode = null;
    this.lastTimestampSource = null;
    this.running = false;
    this.frameHandle = null;
    this.frameCallbackKind = null;
    this.canvas = null;
    this.context = null;
    this.onMessage = null;
    this.onDiagnostic = null;
    this.onFrame = null;
    this.recentFingerprints = new Map();
    this.dedupeMs = 1_500;
  }

  setChipMs(chipMs) {
    assertPositive(chipMs, 'chipMs');
    this.timingMode = 'fixed';
    this.chipMs = chipMs;
    this.autoTimingPlan = null;
    this.estimatedChipMs = chipMs;
    this.decodeIntervalMs = Math.max(100, chipMs * 2);
    this.#refreshRoiRelockGuard();
    this.reset({ keepRoi: true });
  }

  setAutoTiming({ chipMsCandidates = null, chipMsMin = 60, chipMsMax = 260, chipMsStep = 20 } = {}) {
    this.timingMode = 'auto';
    this.chipMs = null;
    this.autoTimingPlan = buildAutoTimingPlan({ chipMsCandidates, chipMsMin, chipMsMax, chipMsStep });
    this.estimatedChipMs = null;
    this.decodeIntervalMs = Math.max(100, this.autoTimingPlan.minimumChipMs * 2);
    this.#refreshRoiRelockGuard();
    this.reset({ keepRoi: true });
  }

  start({ onMessage, onDiagnostic, onFrame } = {}) {
    if (this.running) return this.frameCallbackKind;
    if (!this.video) throw new TypeError('Élément vidéo requis pour start()');
    if (onMessage) this.onMessage = onMessage;
    if (onDiagnostic) this.onDiagnostic = onDiagnostic;
    if (onFrame) this.onFrame = onFrame;
    this.running = true;

    if (typeof this.video.requestVideoFrameCallback === 'function') {
      this.frameCallbackKind = 'requestVideoFrameCallback';
      const tick = (now, metadata = {}) => {
        if (!this.running) return;
        this.#ingestVideoCallback(now, metadata);
        if (this.running) this.frameHandle = this.video.requestVideoFrameCallback(tick);
      };
      this.frameHandle = this.video.requestVideoFrameCallback(tick);
      return this.frameCallbackKind;
    }

    if (typeof this.requestAnimationFrame !== 'function') {
      this.running = false;
      throw new Error('Ni requestVideoFrameCallback ni requestAnimationFrame disponible');
    }
    this.frameCallbackKind = 'requestAnimationFrame-fallback';
    let previousMediaTime = -Infinity;
    const tick = (now) => {
      if (!this.running) return;
      const mediaTime = Number(this.video.currentTime);
      if (Number.isFinite(mediaTime) && mediaTime > previousMediaTime) {
        previousMediaTime = mediaTime;
        this.#ingestVideoCallback(now, { mediaTime, presentedFrames: null });
      }
      if (this.running) this.frameHandle = this.requestAnimationFrame(tick);
    };
    this.frameHandle = this.requestAnimationFrame(tick);
    return this.frameCallbackKind;
  }

  stop() {
    this.running = false;
    if (this.frameHandle !== null) {
      if (this.frameCallbackKind === 'requestVideoFrameCallback'
        && typeof this.video?.cancelVideoFrameCallback === 'function') {
        this.video.cancelVideoFrameCallback(this.frameHandle);
      } else if (this.frameCallbackKind === 'requestAnimationFrame-fallback') {
        this.cancelAnimationFrame?.(this.frameHandle);
      }
    }
    this.frameHandle = null;
    this.frameCallbackKind = null;
  }

  reset({ keepRoi = false } = {}) {
    this.samples = [];
    this.evictedSamples = 0;
    this.nonMonotonicFrames = 0;
    this.lastFrameTimestampMs = -Infinity;
    this.lastDecodeAt = -Infinity;
    this.lastDecode = null;
    this.lastTimestampSource = null;
    this.lastTimingSearch = null;
    this.autoSearchFailures = 0;
    this.nextAutoSearchAt = -Infinity;
    this.roiRelockConsecutiveFailures = 0;
    this.roiRelockTriggeredCount = 0;
    this.lastRoiRelockAt = -Infinity;
    this.roiRelockDiscardedSamples = 0;
    this.lastRoiRelockDecision = null;
    if (this.timingMode === 'auto') this.estimatedChipMs = null;
    this.recentFingerprints.clear();
    if (!keepRoi) this.roiTracker.reset();
  }

  /** Pure/testable ingestion path also used by the video callback. */
  ingestRgba({
    data,
    width,
    height,
    timestampMs,
    timestampSource = 'external',
    presentedFrames = null,
  }) {
    if (!Number.isFinite(timestampMs)) throw new RangeError('timestampMs invalide');
    if (timestampMs <= this.lastFrameTimestampMs) {
      this.nonMonotonicFrames += 1;
      const diagnostic = this.#diagnostic('TIMESTAMP_DROPPED', {
        timestampMs,
        previousTimestampMs: this.lastFrameTimestampMs,
        timestampSource,
      });
      this.onDiagnostic?.(diagnostic);
      return { ok: false, code: 'TIMESTAMP_DROPPED', diagnostic };
    }
    this.lastFrameTimestampMs = timestampMs;
    this.lastTimestampSource = timestampSource;

    const observed = this.roiTracker.observe(data, width, height, timestampMs);
    if (observed.resolutionReset) {
      this.samples = [];
      this.evictedSamples = 0;
    }
    for (const sample of observed.samples) this.#pushSample(sample);

    const frameDiagnostic = this.#diagnostic(observed.locked ? 'ROI_LOCKED' : 'ROI_SEARCH', {
      timestampMs,
      timestampSource,
      presentedFrames,
      roiJustLocked: observed.justLocked,
      roiRelocked: observed.justLocked && this.roiTracker.relockCount > 0,
      luminance: observed.samples.at(-1)?.value ?? null,
    });
    this.onFrame?.(frameDiagnostic);

    let decode = null;
    if (this.autoDecode
      && observed.locked
      && timestampMs - this.lastDecodeAt >= this.decodeIntervalMs
      && (this.timingMode === 'fixed' || timestampMs >= this.nextAutoSearchAt)) {
      decode = this.decodeNow(timestampMs);
    }
    return {
      ok: true,
      code: observed.locked ? 'SAMPLED' : 'ROI_SEARCH',
      roi: this.roiTracker.snapshot(),
      addedSamples: observed.samples.length,
      decode,
      diagnostic: frameDiagnostic,
    };
  }

  decodeNow(nowMs = this.lastFrameTimestampMs) {
    this.lastDecodeAt = nowMs;
    if (!this.roiTracker.locked) {
      const result = { ok: false, code: 'ROI_UNLOCKED', reason: 'Source modulée non verrouillée' };
      this.lastDecode = result;
      this.onDiagnostic?.(this.#diagnostic(result.code, { decode: result }));
      return result;
    }
    const result = this.timingMode === 'auto'
      ? this.#decodeAutoTiming(nowMs)
      : decodeTorchOokSamples(this.samples, {
        chipMs: this.chipMs,
        ...this.decoderOptions,
      });
    if (this.timingMode === 'auto') {
      if (result.ok) {
        this.autoSearchFailures = 0;
        this.nextAutoSearchAt = nowMs + this.autoBackoffBaseMs;
      } else {
        this.autoSearchFailures += 1;
        const backoffMs = Math.min(
          this.autoBackoffMaxMs,
          this.autoBackoffBaseMs * 2 ** Math.min(4, this.autoSearchFailures - 1),
        );
        this.nextAutoSearchAt = nowMs + backoffMs;
      }
    }
    const roiRelockDecision = this.#considerRoiRelock(result, nowMs);
    this.lastDecode = result;
    const diagnostic = this.#diagnostic(result.code, { decode: result, roiRelockDecision });
    this.onDiagnostic?.(diagnostic);
    if (!result.ok) return result;

    this.onFrame?.({ ...diagnostic, ok: true, envelope: result.envelope });
    if (this.#shouldDeliver(result.envelope.fingerprint, nowMs)) this.onMessage?.(result.envelope, result);
    // A complete, CRC-valid frame has consumed the capture. Keep the stable ROI
    // but start a clean timestamp window for the next preamble.
    this.samples = [];
    this.evictedSamples = 0;
    return result;
  }

  getDiagnostics() {
    return this.#diagnostic(this.lastDecode?.code || (this.roiTracker.locked ? 'READY' : 'ROI_SEARCH'));
  }

  #pushSample(sample) {
    this.samples.push({ timestampMs: sample.timestampMs, value: sample.value });
    let removeCount = Math.max(0, this.samples.length - this.maxBufferSamples);
    const cutoff = sample.timestampMs - this.maxBufferMs;
    while (removeCount < this.samples.length && this.samples[removeCount].timestampMs < cutoff) removeCount += 1;
    if (removeCount > 0) {
      this.samples.splice(0, removeCount);
      this.evictedSamples += removeCount;
    }
  }

  #considerRoiRelock(result, nowMs) {
    const first = this.samples[0]?.timestampMs ?? null;
    const last = this.samples.at(-1)?.timestampMs ?? null;
    const spanMs = first !== null && last !== null ? last - first : 0;
    const eligible = !result.ok && ROI_RELOCK_FAILURE_CODES.has(result.code);
    const decision = {
      at: nowMs,
      decodeCode: result.code,
      eligible,
      spanMs,
      action: null,
    };

    if (result.ok) {
      this.roiRelockConsecutiveFailures = 0;
      decision.action = 'VALID_FRAME';
      this.lastRoiRelockDecision = decision;
      return decision;
    }
    if (!eligible) {
      // TRUNCATED/INSUFFICIENT_DURATION/Manchester/CRC evidence protects a
      // possible in-progress frame from a ROI reset.
      this.roiRelockConsecutiveFailures = 0;
      decision.action = 'FRAME_EVIDENCE_PROTECTED';
      this.lastRoiRelockDecision = decision;
      return decision;
    }

    this.roiRelockConsecutiveFailures = Math.min(
      this.roiRelockFailuresRequired,
      this.roiRelockConsecutiveFailures + 1,
    );
    decision.consecutiveFailures = this.roiRelockConsecutiveFailures;
    if (this.roiTracker.explicitRoi || this.roiMaxRelocks === 0) {
      decision.action = 'DISABLED';
    } else if (spanMs < this.roiRelockMinBufferMs) {
      decision.action = 'BUFFER_GUARD';
    } else if (this.roiRelockConsecutiveFailures < this.roiRelockFailuresRequired) {
      decision.action = 'FAILURE_THRESHOLD';
    } else if (this.roiRelockTriggeredCount >= this.roiMaxRelocks) {
      decision.action = 'MAX_RELOCKS';
    } else if (nowMs - this.lastRoiRelockAt < this.roiRelockCooldownMs) {
      decision.action = 'COOLDOWN';
    } else {
      const discardedSamples = this.samples.length;
      const started = this.roiTracker.requestRelock(nowMs, result.code);
      if (started) {
        this.roiRelockTriggeredCount += 1;
        this.lastRoiRelockAt = nowMs;
        this.roiRelockDiscardedSamples += discardedSamples;
        this.roiRelockConsecutiveFailures = 0;
        this.samples = [];
        this.lastTimingSearch = null;
        this.autoSearchFailures = 0;
        this.nextAutoSearchAt = -Infinity;
        if (this.timingMode === 'auto') this.estimatedChipMs = null;
        decision.action = 'RELOCK_STARTED';
        decision.discardedSamples = discardedSamples;
      } else {
        decision.action = 'TRACKER_REFUSED';
      }
    }
    this.lastRoiRelockDecision = decision;
    return decision;
  }

  #refreshRoiRelockGuard() {
    const maximumChipMs = this.timingMode === 'fixed'
      ? this.chipMs
      : this.autoTimingPlan.maximumChipMs;
    // The Manchester preamble + sync occupies 128 chips. Waiting at least 126
    // chip durations matches the decoder's own minimum-duration gate.
    this.roiRelockMinBufferMs = this.roiRelockMinBufferOverrideMs
      ?? maximumChipMs * 126;
  }

  #shouldDeliver(fingerprint, nowMs) {
    const previous = this.recentFingerprints.get(fingerprint) ?? -Infinity;
    this.recentFingerprints.set(fingerprint, nowMs);
    for (const [key, timestamp] of this.recentFingerprints) {
      if (nowMs - timestamp > this.dedupeMs * 4) this.recentFingerprints.delete(key);
    }
    return nowMs - previous >= this.dedupeMs;
  }

  #diagnostic(code, extra = {}) {
    const first = this.samples[0]?.timestampMs ?? null;
    const last = this.samples.at(-1)?.timestampMs ?? null;
    return {
      ok: code === 'OK' || code === 'READY' || code === 'ROI_LOCKED',
      code,
      profile: TORCH_OOK_PROFILE,
      standardConformance: 'none-claimed',
      timingMode: this.timingMode,
      chipMs: this.chipMs,
      estimatedChipMs: this.estimatedChipMs,
      timingCandidates: this.lastTimingSearch,
      nextAutoSearchAt: this.timingMode === 'auto' ? this.nextAutoSearchAt : null,
      timestampSource: this.lastTimestampSource,
      nonMonotonicFrames: this.nonMonotonicFrames,
      roi: this.roiTracker.snapshot(),
      roiRelock: {
        enabled: !this.roiTracker.explicitRoi && this.roiMaxRelocks > 0,
        eligibleDecodeCodes: [...ROI_RELOCK_FAILURE_CODES],
        consecutiveFailures: this.roiRelockConsecutiveFailures,
        failuresRequired: this.roiRelockFailuresRequired,
        minimumBufferMs: this.roiRelockMinBufferMs,
        cooldownMs: this.roiRelockCooldownMs,
        maxRelocks: this.roiMaxRelocks,
        triggeredCount: this.roiRelockTriggeredCount,
        lastRelockAt: Number.isFinite(this.lastRoiRelockAt) ? this.lastRoiRelockAt : null,
        discardedSamples: this.roiRelockDiscardedSamples,
        lastDecision: this.lastRoiRelockDecision ? { ...this.lastRoiRelockDecision } : null,
      },
      buffer: {
        sampleCount: this.samples.length,
        maxSamples: this.maxBufferSamples,
        maxDurationMs: this.maxBufferMs,
        evictedSamples: this.evictedSamples,
        firstTimestampMs: first,
        lastTimestampMs: last,
        spanMs: first !== null && last !== null ? last - first : 0,
      },
      ...extra,
    };
  }

  #ingestVideoCallback(now, metadata) {
    try {
      const frame = this.frameReader
        ? this.frameReader(this.video)
        : this.#readVideoFrame();
      const resolved = resolveFrameTimestamp(now, metadata);
      this.ingestRgba({
        data: frame.data,
        width: frame.width,
        height: frame.height,
        timestampMs: resolved.timestampMs,
        timestampSource: resolved.source,
        presentedFrames: metadata.presentedFrames ?? null,
      });
    } catch (error) {
      this.onDiagnostic?.(this.#diagnostic('FRAME_READ_FAILED', {
        reason: errorMessage(error),
      }));
    }
  }

  #decodeAutoTiming(nowMs) {
    const coarse = this.autoTimingPlan.coarseCandidates.map((candidateChipMs) => (
      probeTorchOokPreamble(this.samples, {
        chipMs: candidateChipMs,
        phaseSteps: this.autoProbePhaseSteps,
      })
    ));
    coarse.sort(compareProbe);
    const seeds = coarse.slice(0, this.autoRefineSeeds);
    if (this.estimatedChipMs !== null) {
      seeds.unshift({ chipMs: this.estimatedChipMs, combinedScore: 2 });
    }
    const refinedValues = buildRefinedCandidates(seeds, this.autoTimingPlan);
    const refined = refinedValues.map((candidateChipMs) => (
      probeTorchOokPreamble(this.samples, {
        chipMs: candidateChipMs,
        phaseSteps: this.autoProbePhaseSteps,
      })
    ));
    refined.sort(compareProbe);
    const fineValues = buildFineCandidates(refined.slice(0, 2), this.autoTimingPlan);
    const fine = fineValues.map((candidateChipMs) => (
      probeTorchOokPreamble(this.samples, {
        chipMs: candidateChipMs,
        phaseSteps: this.autoProbePhaseSteps,
      })
    ));
    fine.sort(compareProbe);

    // Never spend full-frame CPU on arbitrary noise: a real candidate must at
    // least show a strong Manchester preamble before reaching CRC validation.
    const decodeTargets = fine
      .filter((probe) => probe.preambleScore >= 0.78 && probe.syncScore >= 0.60)
      .slice(0, this.autoDecodeCandidates);
    const decodedCandidates = [];
    let selected = null;
    for (const probe of decodeTargets) {
      const decoded = decodeTorchOokSamples(this.samples, {
        chipMs: probe.chipMs,
        ...this.decoderOptions,
      });
      decodedCandidates.push({
        chipMs: probe.chipMs,
        code: decoded.code,
        ok: decoded.ok,
        preambleScore: probe.preambleScore,
        syncScore: probe.syncScore,
      });
      if (decoded.ok) {
        selected = { decoded, probe };
        break;
      }
    }

    this.lastTimingSearch = {
      searchedAt: nowMs,
      coarseCount: coarse.length,
      refinedCount: refined.length,
      fineCount: fine.length,
      fullDecodeCount: decodedCandidates.length,
      maximumFullDecodes: this.autoDecodeCandidates,
      coarse: coarse.slice(0, 6).map(compactProbe),
      refined: refined.slice(0, 8).map(compactProbe),
      fine: fine.slice(0, 8).map(compactProbe),
      decoded: decodedCandidates,
    };
    if (!selected) {
      return {
        ok: false,
        code: 'AUTO_TIMING_NOT_FOUND',
        reason: 'Aucune durée candidate ne produit préambule, sync et CRC32C valides',
        diagnostics: {
          estimatedChipMs: null,
          candidates: this.lastTimingSearch,
        },
      };
    }
    this.estimatedChipMs = selected.probe.chipMs;
    return {
      ...selected.decoded,
      diagnostics: {
        ...selected.decoded.diagnostics,
        estimatedChipMs: this.estimatedChipMs,
        candidates: this.lastTimingSearch,
      },
    };
  }

  #readVideoFrame() {
    if ((this.video.readyState ?? 0) < 2) throw new Error('Frame vidéo indisponible');
    const sourceWidth = this.video.videoWidth;
    const sourceHeight = this.video.videoHeight;
    if (!Number.isFinite(sourceWidth) || !Number.isFinite(sourceHeight) || sourceWidth < 1 || sourceHeight < 1) {
      throw new Error('Dimensions vidéo indisponibles');
    }
    const width = Math.min(this.analysisWidth, sourceWidth);
    const height = Math.max(1, Math.round(width * sourceHeight / sourceWidth));
    if (!this.canvas) {
      this.canvas = this.canvasFactory();
      this.context = this.canvas.getContext('2d', { alpha: false, willReadFrequently: true });
      if (!this.context) throw new Error('Contexte canvas 2D indisponible');
    }
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    this.context.drawImage(this.video, 0, 0, width, height);
    const image = this.context.getImageData(0, 0, width, height);
    return { data: image.data, width, height };
  }
}

export class StableTorchRoi {
  constructor({
    gridColumns = 8,
    gridRows = 6,
    explicitRoi = null,
    lockFrames = 8,
    minTemporalContrast = 0.08,
    maxHistoryFrames = 240,
  } = {}) {
    assertIntegerAtLeast(gridColumns, 1, 'gridColumns');
    assertIntegerAtLeast(gridRows, 1, 'gridRows');
    assertIntegerAtLeast(lockFrames, 2, 'lockFrames');
    assertPositive(minTemporalContrast, 'minTemporalContrast');
    assertIntegerAtLeast(maxHistoryFrames, lockFrames, 'maxHistoryFrames');
    this.gridColumns = gridColumns;
    this.gridRows = gridRows;
    this.explicitRoi = explicitRoi ? validateNormalizedRoi(explicitRoi) : null;
    this.lockFrames = lockFrames;
    this.minTemporalContrast = minTemporalContrast;
    this.maxHistoryFrames = maxHistoryFrames;
    this.reset();
  }

  reset() {
    this.locked = false;
    this.roi = null;
    this.tileIndex = null;
    this.width = null;
    this.height = null;
    this.frameCount = 0;
    this.history = [];
    this.minima = null;
    this.maxima = null;
    this.temporalContrast = 0;
    this.lockedAt = null;
    this.lockCount = 0;
    this.relockCount = 0;
    this.previousTileIndex = null;
    this.lastRelockAt = null;
    this.lastRelockReason = null;
  }

  requestRelock(timestampMs, reason = 'decoder-failure') {
    if (this.explicitRoi || !this.locked || !Number.isFinite(timestampMs)) return false;
    this.previousTileIndex = this.tileIndex;
    this.locked = false;
    this.roi = null;
    this.tileIndex = null;
    this.frameCount = 0;
    this.history = [];
    this.minima = null;
    this.maxima = null;
    this.temporalContrast = 0;
    this.lockedAt = null;
    this.relockCount += 1;
    this.lastRelockAt = timestampMs;
    this.lastRelockReason = String(reason || 'decoder-failure');
    return true;
  }

  observe(data, width, height, timestampMs) {
    validateRgba(data, width, height);
    let resolutionReset = false;
    if (this.width !== null && (this.width !== width || this.height !== height)) {
      this.reset();
      resolutionReset = true;
    }
    this.width = width;
    this.height = height;

    if (this.explicitRoi) {
      const justLocked = !this.locked;
      if (justLocked) {
        this.locked = true;
        this.roi = normalizedToPixelRoi(this.explicitRoi, width, height);
        this.lockedAt = timestampMs;
        this.lockCount += 1;
      }
      return {
        locked: true,
        justLocked,
        resolutionReset,
        samples: [{ timestampMs, value: aggregateRoiLuminance(data, width, height, this.roi) }],
      };
    }

    const tileData = calculateTileMeans(data, width, height, this.gridColumns, this.gridRows);
    if (this.locked) {
      return {
        locked: true,
        justLocked: false,
        resolutionReset,
        samples: [{ timestampMs, value: tileData.means[this.tileIndex] }],
      };
    }

    this.frameCount += 1;
    this.history.push({ timestampMs, means: tileData.means });
    if (this.history.length > this.maxHistoryFrames) this.history.shift();
    if (!this.minima) {
      this.minima = Float64Array.from(tileData.means);
      this.maxima = Float64Array.from(tileData.means);
    } else {
      for (let index = 0; index < tileData.means.length; index += 1) {
        this.minima[index] = Math.min(this.minima[index], tileData.means[index]);
        this.maxima[index] = Math.max(this.maxima[index], tileData.means[index]);
      }
    }

    if (this.frameCount < this.lockFrames) {
      return { locked: false, justLocked: false, resolutionReset, samples: [] };
    }
    let bestIndex = 0;
    let bestContrast = -Infinity;
    for (let index = 0; index < tileData.means.length; index += 1) {
      const contrast = this.maxima[index] - this.minima[index];
      if (contrast > bestContrast) {
        bestContrast = contrast;
        bestIndex = index;
      }
    }
    this.temporalContrast = bestContrast;
    if (bestContrast < this.minTemporalContrast) {
      return { locked: false, justLocked: false, resolutionReset, samples: [] };
    }

    this.locked = true;
    this.tileIndex = bestIndex;
    this.roi = tileData.bounds[bestIndex];
    this.lockedAt = timestampMs;
    this.lockCount += 1;
    const samples = this.history.map((entry) => ({
      timestampMs: entry.timestampMs,
      value: entry.means[bestIndex],
    }));
    this.history = [];
    return { locked: true, justLocked: true, resolutionReset, samples };
  }

  snapshot() {
    return {
      locked: this.locked,
      mode: this.explicitRoi ? 'explicit' : 'temporal-grid',
      roi: this.roi ? { ...this.roi } : null,
      tileIndex: this.tileIndex,
      gridColumns: this.gridColumns,
      gridRows: this.gridRows,
      observedFrames: this.frameCount,
      temporalContrast: this.temporalContrast,
      minimumTemporalContrast: this.minTemporalContrast,
      lockedAt: this.lockedAt,
      lockCount: this.lockCount,
      relockCount: this.relockCount,
      previousTileIndex: this.previousTileIndex,
      lastRelockAt: this.lastRelockAt,
      lastRelockReason: this.lastRelockReason,
      resolution: this.width && this.height ? { width: this.width, height: this.height } : null,
    };
  }
}

export function calculateTileMeans(data, width, height, columns, rows) {
  validateRgba(data, width, height);
  assertIntegerAtLeast(columns, 1, 'columns');
  assertIntegerAtLeast(rows, 1, 'rows');
  const means = new Float64Array(columns * rows);
  const counts = new Uint32Array(columns * rows);
  const bounds = [];
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      bounds.push(tileBounds(column, row, columns, rows, width, height));
    }
  }
  for (let y = 0; y < height; y += 1) {
    const row = Math.min(rows - 1, Math.floor(y * rows / height));
    for (let x = 0; x < width; x += 1) {
      const column = Math.min(columns - 1, Math.floor(x * columns / width));
      const tile = row * columns + column;
      const offset = (y * width + x) * 4;
      means[tile] += rgbaLuminance(data, offset);
      counts[tile] += 1;
    }
  }
  for (let index = 0; index < means.length; index += 1) means[index] /= counts[index] || 1;
  return { means, counts, bounds };
}

export function aggregateRoiLuminance(data, width, height, roi) {
  validateRgba(data, width, height);
  const pixelRoi = validatePixelRoi(roi, width, height);
  const values = [];
  for (let y = pixelRoi.y; y < pixelRoi.y + pixelRoi.height; y += 1) {
    for (let x = pixelRoi.x; x < pixelRoi.x + pixelRoi.width; x += 1) {
      values.push(rgbaLuminance(data, (y * width + x) * 4));
    }
  }
  values.sort((a, b) => a - b);
  const trim = Math.floor(values.length * 0.1);
  const selected = values.slice(trim, Math.max(trim + 1, values.length - trim));
  return selected.reduce((sum, value) => sum + value, 0) / selected.length;
}

export function resolveFrameTimestamp(now, metadata = {}) {
  const mediaTime = Number(metadata.mediaTime);
  if (Number.isFinite(mediaTime) && mediaTime >= 0) {
    return { timestampMs: mediaTime * 1000, source: 'rVFC.mediaTime' };
  }
  if (!Number.isFinite(now)) throw new RangeError('timestamp callback invalide');
  return { timestampMs: now, source: 'callback-now' };
}

export function buildAutoTimingPlan({
  chipMsCandidates = null,
  chipMsMin = 60,
  chipMsMax = 260,
  chipMsStep = 20,
} = {}) {
  let coarseCandidates;
  if (chipMsCandidates !== null) {
    if (!Array.isArray(chipMsCandidates) || chipMsCandidates.length < 1 || chipMsCandidates.length > 32) {
      throw new RangeError('chipMsCandidates doit contenir entre 1 et 32 valeurs');
    }
    coarseCandidates = [...new Set(chipMsCandidates.map((value) => {
      assertPositive(value, 'chipMsCandidates[]');
      return roundTiming(value);
    }))].sort((a, b) => a - b);
  } else {
    assertPositive(chipMsMin, 'chipMsMin');
    assertPositive(chipMsMax, 'chipMsMax');
    assertPositive(chipMsStep, 'chipMsStep');
    if (chipMsMax < chipMsMin) throw new RangeError('chipMsMax inférieur à chipMsMin');
    coarseCandidates = [];
    for (let value = chipMsMin; value <= chipMsMax + 1e-9; value += chipMsStep) {
      coarseCandidates.push(roundTiming(value));
      if (coarseCandidates.length > 32) throw new RangeError('grille chipMs limitée à 32 candidats grossiers');
    }
    if (coarseCandidates.at(-1) < chipMsMax && coarseCandidates.length < 32) {
      coarseCandidates.push(roundTiming(chipMsMax));
    }
  }
  const spacings = [];
  for (let index = 1; index < coarseCandidates.length; index += 1) {
    spacings.push(coarseCandidates[index] - coarseCandidates[index - 1]);
  }
  const coarseStepMs = spacings.length
    ? Math.min(...spacings)
    : Math.max(4, coarseCandidates[0] * 0.1);
  return Object.freeze({
    coarseCandidates: Object.freeze(coarseCandidates),
    minimumChipMs: coarseCandidates[0],
    maximumChipMs: coarseCandidates.at(-1),
    coarseStepMs,
    refineStepMs: Math.max(1, roundTiming(coarseStepMs / 4)),
    refineRadiusMs: coarseStepMs,
    fineStepMs: 1,
    fineRadiusMs: Math.max(2, roundTiming(coarseStepMs / 4)),
    maximumCoarseCandidates: 32,
    maximumRefinedCandidates: 32,
  });
}

function buildRefinedCandidates(seeds, plan) {
  const centers = seeds.map((seed) => seed.chipMs)
    .filter((value) => Number.isFinite(value));
  const values = new Set();
  for (const center of centers) {
    values.add(roundTiming(center));
    for (
      let value = center - plan.refineRadiusMs;
      value <= center + plan.refineRadiusMs + 1e-9;
      value += plan.refineStepMs
    ) {
      if (value >= plan.minimumChipMs && value <= plan.maximumChipMs) values.add(roundTiming(value));
    }
  }
  return [...values]
    .sort((left, right) => nearestDistance(left, centers) - nearestDistance(right, centers))
    .slice(0, plan.maximumRefinedCandidates);
}

function buildFineCandidates(seeds, plan) {
  const centers = seeds.map((seed) => seed.chipMs).filter(Number.isFinite);
  const values = new Set(centers.map(roundTiming));
  for (const center of centers) {
    for (
      let value = center - plan.fineRadiusMs;
      value <= center + plan.fineRadiusMs + 1e-9;
      value += plan.fineStepMs
    ) {
      if (value >= plan.minimumChipMs && value <= plan.maximumChipMs) values.add(roundTiming(value));
    }
  }
  return [...values]
    .sort((left, right) => nearestDistance(left, centers) - nearestDistance(right, centers))
    .slice(0, plan.maximumRefinedCandidates);
}

function nearestDistance(value, centers) {
  return Math.min(...centers.map((center) => Math.abs(value - center)));
}

function compareProbe(left, right) {
  return right.combinedScore - left.combinedScore
    || right.syncScore - left.syncScore
    || right.preambleScore - left.preambleScore
    || left.chipMs - right.chipMs;
}

function compactProbe(probe) {
  return {
    chipMs: probe.chipMs,
    preambleScore: roundScore(probe.preambleScore),
    syncScore: roundScore(probe.syncScore),
    combinedScore: roundScore(probe.combinedScore),
    phaseMs: Number.isFinite(probe.phaseMs) ? roundTiming(probe.phaseMs) : null,
    startChip: probe.startChip,
  };
}

function roundTiming(value) {
  return Math.round(value * 1000) / 1000;
}

function roundScore(value) {
  return Math.round((value || 0) * 10_000) / 10_000;
}

function tileBounds(column, row, columns, rows, width, height) {
  const x0 = Math.floor(column * width / columns);
  const x1 = Math.floor((column + 1) * width / columns);
  const y0 = Math.floor(row * height / rows);
  const y1 = Math.floor((row + 1) * height / rows);
  return { x: x0, y: y0, width: Math.max(1, x1 - x0), height: Math.max(1, y1 - y0) };
}

function rgbaLuminance(data, offset) {
  return (data[offset] * 0.2126 + data[offset + 1] * 0.7152 + data[offset + 2] * 0.0722) / 255;
}

function validateRgba(data, width, height) {
  assertIntegerAtLeast(width, 1, 'width');
  assertIntegerAtLeast(height, 1, 'height');
  if (!data || typeof data.length !== 'number' || data.length < width * height * 4) {
    throw new RangeError('buffer RGBA trop court');
  }
}

function validateNormalizedRoi(roi) {
  const values = ['x', 'y', 'width', 'height'].map((key) => Number(roi?.[key]));
  if (values.some((value) => !Number.isFinite(value))
    || values[0] < 0 || values[1] < 0 || values[2] <= 0 || values[3] <= 0
    || values[0] + values[2] > 1 || values[1] + values[3] > 1) {
    throw new RangeError('ROI normalisée invalide');
  }
  return { x: values[0], y: values[1], width: values[2], height: values[3] };
}

function normalizedToPixelRoi(roi, width, height) {
  const x = Math.floor(roi.x * width);
  const y = Math.floor(roi.y * height);
  const right = Math.max(x + 1, Math.ceil((roi.x + roi.width) * width));
  const bottom = Math.max(y + 1, Math.ceil((roi.y + roi.height) * height));
  return validatePixelRoi({ x, y, width: right - x, height: bottom - y }, width, height);
}

function validatePixelRoi(roi, width, height) {
  const x = Math.max(0, Math.floor(Number(roi?.x)));
  const y = Math.max(0, Math.floor(Number(roi?.y)));
  const right = Math.min(width, Math.ceil(Number(roi?.x) + Number(roi?.width)));
  const bottom = Math.min(height, Math.ceil(Number(roi?.y) + Number(roi?.height)));
  if (![x, y, right, bottom].every(Number.isFinite) || right <= x || bottom <= y) {
    throw new RangeError('ROI pixel invalide');
  }
  return { x, y, width: right - x, height: bottom - y };
}

function defaultCanvasFactory() {
  if (!globalThis.document?.createElement) throw new Error('Canvas indisponible');
  return globalThis.document.createElement('canvas');
}

function assertPositive(value, name) {
  if (!Number.isFinite(value) || value <= 0) throw new RangeError(`${name} doit être > 0`);
}

function assertIntegerAtLeast(value, minimum, name) {
  if (!Number.isInteger(value) || value < minimum) throw new RangeError(`${name} doit être >= ${minimum}`);
}

function assertIntegerBetween(value, minimum, maximum, name) {
  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    throw new RangeError(`${name} doit être entre ${minimum} et ${maximum}`);
  }
}

function errorMessage(error) {
  return error instanceof Error && error.message ? error.message : String(error || 'erreur inconnue');
}
